/**
 * sl-ya4v.15: useRenameBrandedSubdomain — wraps the `renameBrandedSubdomain`
 * and `releaseBrandedSubdomain` mutations and exposes them with a shared
 * `isPending` flag plus invalidation of the SWR caches the hook owns.
 *
 * The hook invalidates the two SWR-backed caches it knows about — the public-
 * host snapshot and the rename-availability gate. The custom-domains entity
 * list does not use SWR (`useSettingsEntityList`); the caller refreshes it
 * via the `refresh` callback returned by that hook.
 */
import { GraphQLService } from '@affine/core/modules/cloud';
import {
  releaseBrandedSubdomainMutation,
  renameBrandedSubdomainMutation,
} from '@affine/graphql';
import { useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';

export interface UseRenameBrandedSubdomainResult {
  rename: (newLabel: string) => Promise<{ effectiveHost: string }>;
  release: () => Promise<{ effectiveHost: string }>;
  isPending: boolean;
}

export function useRenameBrandedSubdomain(
  workspaceId: string
): UseRenameBrandedSubdomainResult {
  const graphql = useService(GraphQLService);
  const { mutate } = useSWRConfig();
  const [isPending, setIsPending] = useState(false);

  const invalidate = useCallback(async () => {
    await Promise.all([
      mutate(['workspacePublicHost', workspaceId]),
      mutate(['brandedSubdomainRenameAvailability', workspaceId]),
    ]);
  }, [mutate, workspaceId]);

  const rename = useCallback(
    async (newLabel: string) => {
      setIsPending(true);
      try {
        const res = await graphql.gql({
          query: renameBrandedSubdomainMutation,
          variables: { workspaceId, newLabel },
        });
        await invalidate();
        return res.renameBrandedSubdomain;
      } finally {
        setIsPending(false);
      }
    },
    [graphql, workspaceId, invalidate]
  );

  const release = useCallback(async () => {
    setIsPending(true);
    try {
      const res = await graphql.gql({
        query: releaseBrandedSubdomainMutation,
        variables: { workspaceId },
      });
      await invalidate();
      return res.releaseBrandedSubdomain;
    } finally {
      setIsPending(false);
    }
  }, [graphql, workspaceId, invalidate]);

  return { rename, release, isPending };
}
